import { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { useToast } from '../../../../../context';
import CareMapprButton from '../../../../components/Button/CareMapprButton';
import { useMedicationProfileCatalogue } from '../../context/MedicationProfileCatalogueProvider';
import type { MedicationCatalog } from '../../../../components/Users/MedicationOrderSets/types';
import { useInitialMeds } from '../../hook/useInitialMeds';

interface CreateMedProfileCatalogueSectionProps {
  onClose: () => void;
}

const CreateMedProfileCatalogueSection = ({
  onClose,
}: CreateMedProfileCatalogueSectionProps) => {
  const { setToast } = useToast();
  const { initialMeds } = useInitialMeds();
  const {
    medCatalogData,
    isLoadingCatalogue,
    errorMessage,
    addSelectedCatalogMedsToSections,
  } = useMedicationProfileCatalogue();

  const [selectedByTitle, setSelectedByTitle] = useState<
    Record<string, MedicationCatalog | null>
  >({});
  const autoCompletedRef = useRef(false);
  const normalize = (value: unknown) =>
    String(value ?? '')
      .trim()
      .toLowerCase();

  const initialMedsByMpId = useMemo(() => {
    const map = new Map<string, { furtherInformation?: string }>();
    for (const medication of initialMeds) {
      const mpId = medication?.amt?.mpId;
      if (!mpId) continue;
      map.set(String(mpId), {
        furtherInformation: medication?.furtherInformation,
      });
    }
    return map;
  }, [initialMeds]);

  const initialMedsByName = useMemo(() => {
    const map = new Map<string, { furtherInformation?: string }>();
    for (const medication of initialMeds) {
      const medName = medication?.medName;
      if (!medName) continue;
      map.set(normalize(medName), {
        furtherInformation: medication?.furtherInformation,
      });
    }
    return map;
  }, [initialMeds]);

  const onAddMedications = () => {
    const selected = Object.values(selectedByTitle).filter(Boolean) as
      | MedicationCatalog[]
      | [];
    addSelectedCatalogMedsToSections(selected);
    onClose();
  };

  useEffect(() => {
    if (!errorMessage) return;
    setToast('Cannot get medication catalogue');
  }, [errorMessage, setToast]);

  useEffect(() => {
    if (autoCompletedRef.current) return;
    if (isLoadingCatalogue) return;
    if (!medCatalogData || medCatalogData.length === 0) return;

    const titles: string[] = [];
    const selectedMeds: MedicationCatalog[] = [];
    let allSingleOption = true;

    for (const group of medCatalogData) {
      const title = Object.keys(group ?? {})?.[0];
      if (!title) continue;
      titles.push(title);
      const options = (group as any)[title];
      const meds = Array.isArray(options) ? options : [];
      if (meds.length !== 1) {
        allSingleOption = false;
        continue;
      }
      selectedMeds.push(meds[0]);
    }

    if (titles.length === 0) return;

    setSelectedByTitle((prev) => {
      const next = { ...prev };
      for (const group of medCatalogData) {
        const title = Object.keys(group ?? {})?.[0];
        if (!title) continue;
        const options = (group as any)[title];
        const meds = Array.isArray(options) ? options : [];
        if (meds.length === 1 && !next[title]) {
          next[title] = meds[0];
        }
      }
      return next;
    });

    if (allSingleOption && selectedMeds.length === titles.length) {
      autoCompletedRef.current = true;
      addSelectedCatalogMedsToSections(selectedMeds);
      onClose();
    }
  }, [
    addSelectedCatalogMedsToSections,
    isLoadingCatalogue,
    medCatalogData,
    onClose,
  ]);

  if (errorMessage) return null;

  return (
    <div className="py-2 d-flex flex-column gap-3">
      {isLoadingCatalogue ? (
        <LoadingSpinner showText="" size={20} />
      ) : (
        medCatalogData.length != 0 && (
          <>
            <span
              className="heading2"
              style={{
                fontWeight: 600,
                color: 'rgb(51, 51, 51)',
              }}
            >
              Medication with medicine catalog matches
            </span>
            <span>Please select an option for each med</span>
            {medCatalogData?.map((item, index) => {
              const title = Object.keys(item)?.[0];
              const meds =
                title && Array.isArray((item as any)[title])
                  ? ((item as any)[title] as MedicationCatalog[])
                  : [];
              const medOptions = [
                { label: '', value: null },
                ...meds.map((med) => ({
                  label: med.med_name,
                  value: med,
                })),
              ];
              const selectedMed = title ? selectedByTitle[title] : null;
              const selectedOption =
                selectedMed && title
                  ? (medOptions.find((o) => o.value?.id === selectedMed.id) ??
                    null)
                  : null;
              const titleDosageInstruction =
                meds
                  .map((med) => med?.mp_id_from_med_profile ?? med?.mp_id)
                  .map((value) =>
                    value && initialMedsByMpId.has(String(value))
                      ? initialMedsByMpId.get(String(value))?.furtherInformation
                      : undefined,
                  )
                  .find(Boolean) ??
                initialMedsByName.get(normalize(title))?.furtherInformation;
              return (
                <div
                  key={`med-profile-catalog-${index}`}
                  className="d-flex flex-column gap-2"
                >
                  <div className="my-2">
                    <span className="medication-row-label">{title}</span>
                    {titleDosageInstruction ? (
                      <div
                        style={{
                          margin: '4px 0',
                          color: '#5C6469',
                          fontSize: 12,
                          fontWeight: 400,
                          whiteSpace: 'normal',
                          wordWrap: 'break-word',
                        }}
                      >
                        {titleDosageInstruction}
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <div className="divSelect">
                      <Select
                        options={medOptions}
                        onChange={(selectedOption) => {
                          if (!title) return;
                          setSelectedByTitle((prev) => ({
                            ...prev,
                            [title]: (selectedOption as any)?.value ?? null,
                          }));
                        }}
                        isMulti={false}
                        value={selectedOption as any}
                        isClearable
                        placeholder="Please select the med"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )
      )}
      {!isLoadingCatalogue && medCatalogData.length != 0 && (
        <CareMapprButton
          extraClassNames="width-33-percent"
          onClick={onAddMedications}
          disabled={!medCatalogData || medCatalogData.length === 0}
        >
          Add medications
        </CareMapprButton>
      )}
    </div>
  );
};

export default CreateMedProfileCatalogueSection;
